export enum ResponseType {
  TEXT = 0,
  JSON = 1,
}

export interface HttpRequestOptions extends Omit<RequestInit, 'method' | 'body'> {
  responseType?: ResponseType;
}

const getResponse = async <HttpResponse>(
  response: Response,
  responseType: ResponseType,
): Promise<HttpResponse> => {
  const options = {
    [ResponseType.JSON]: async () => {
      try {
        return (await response.json()) as HttpResponse;
      } catch (error) {
        return {} as HttpResponse;
      }
    },
    [ResponseType.TEXT]: () => response.text() as unknown as HttpResponse,
    default: () => {
      throw new Error('Response type unsupported');
    },
  };

  return options[responseType] ? options[responseType]() : options.default();
};

const sendRequest = async <HttpResponse>(
  url: string,
  responseType = ResponseType.JSON,
  options?: RequestInit,
): Promise<HttpResponse> => {
  const response: Response = await window.fetch(url, options);

  if (!response.ok) {
    throw new Error(response.statusText);
  }

  return getResponse<HttpResponse>(response, responseType);
};

const post = <HttpBody, HttpResponse>(
  url: string,
  body: HttpBody,
  options?: HttpRequestOptions,
): Promise<HttpResponse> =>
  sendRequest(url, options?.responseType, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });

const get = <HttpResponse>(url: string, options?: HttpRequestOptions): Promise<HttpResponse> =>
  sendRequest(url, options?.responseType, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });

export const HttpService = {
  get,
  post,
};
